import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { capitalize } from "@mui/material";
import { SendPostRequest } from "../../http";
import FooterWrapper from "../Footer/FooterWrapper";
import HeaderWrapper from "../Header/HeaderWrapper";
import TextMainBannerWrapper from "../TextMainBanners/TextMainBannerWrapper";
import { toast } from "react-toastify";

function Style4Contactus() {
  const [contactData, setContactData] = useState([]);
  const sitedata = useSelector((store) => store.global.loginData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const [contactForm, setContactForm] = useState({
    fullname: "",
    email: "",
    phone: "",
    message: "",
  });

  // Function to extract src from iframe HTML
  const extractMapSrc = (iframeHtml) => {
    if (!iframeHtml) return null;
    const srcMatch = iframeHtml.match(/src="([^"]*)"/);
    return srcMatch ? srcMatch[1] : null;
  };

  // Function to open Google Map in new tab
  const openGoogleMap = () => {
    if (contactData && contactData.length > 0 && contactData[0].google_map) {
      const mapSrc = extractMapSrc(contactData[0].google_map);
      if (mapSrc) {
        window.open(mapSrc, '_blank');
      } else {
        // Fallback to address search if iframe src not found
        if (sitedata?.site_address) {
          const encodedAddress = encodeURIComponent(sitedata.site_address);
          window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
        }
      }
    }
  };

  // 📌 Email Regex
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // 📌 Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.fullname) {
      toast.error("Please enter your full name");
      return;
    } else if (!contactForm.email) {
      toast.error("Please enter your email");
      return;
    } else if (!validateEmail(contactForm.email)) {
      toast.error("Please enter a valid email");
      return;
    } else if (!contactForm.phone) {
      toast.error("Please enter your phone number");
      return;
    } else if (
      contactForm?.phone?.length > 14 ||
      contactForm?.phone?.length < 10
    ) {
      toast.error("Invalid Contact Number");
      return;
    } else if (contactForm?.message === "") {
      toast.error("Please enter your message");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await SendPostRequest("contact-us-form", contactForm);
      formRef.current.reset();
      if (response.status === "success") {
        setContactForm({
          fullname: "",
          email: "",
          phone: "",
          message: "",
        });
        toast.success(response?.message);
      } else {
        let errorMessage = response?.message || "An error occurred";
        toast.error(`Error: ${errorMessage}`);
        console.error("Error fetching data:", errorMessage);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      console.error("Error fetching data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 📌 Handle Input Change (Auto add +92 if not included)
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For phone input only
    if (name === "phone") {
      let formattedValue = value;

      // If user starts typing with digits, auto-add +92
      if (/^[0-9]/.test(value) && !value.startsWith("+92")) {
        formattedValue = "+92" + value.replace(/^0+/, ""); // remove leading 0
      }

      setContactForm((prevData) => ({
        ...prevData,
        [name]: formattedValue,
      }));
    } else {
      setContactForm((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getContactData();
  }, []);

  const getContactData = async () => {
    const response = await SendPostRequest("content/contactus");
    setContactData(response?.data);
  };

  return (
    <div className="page-wrapper-inner">
      <div className="page-wrapper-inner">
        <HeaderWrapper />
        <TextMainBannerWrapper Heading="Contact Us" Image="theme-2-banner-contactus.webp" />

        <div id="section-contact" className="section-contact pt-5">
          <div className="container">
            <div className="row">
              <div className="col-md-12 contact-info">
                <div className="row text-center">
                  <div className="col-md-4 mb-4">
                    <div className="feature-box-wrap f-box-style-2 text-center relative style4-contact-icons">
                      <div className="feature-box-details">
                        <div className="feature-icon margin-bottom-20">
                          <span className="bi bi-pin-map b-radius-50 d-block"></span>
                        </div>
                        <div className="feature-content">
                          <div className="feature-title relative margin-bottom-10">
                            <h5>Address</h5>
                          </div>
                          <span className="mb-0">
                            <span
                              style={{ cursor: 'pointer', textDecoration: 'none' }}
                              onClick={openGoogleMap}
                            >
                              {sitedata?.site_address || "--"}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-4">
                    <div className="feature-box-wrap f-box-style-2 text-center relative style4-contact-icons">
                      <div className="feature-box-details">
                        <div className="feature-icon margin-bottom-20">
                          <span className="bi bi-headset b-radius-50 d-block"></span>
                        </div>
                        <div className="feature-content">
                          <div className="feature-title relative margin-bottom-10">
                            <h5>Phone Number</h5>
                          </div>
                          <span className="mb-0">
                            <a href={`tel:${sitedata?.site_contact}`}>
                              {sitedata?.site_contact || "--"}
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-4">
                    <div className="feature-box-wrap f-box-style-2 text-center relative style4-contact-icons">
                      <div className="feature-box-details">
                        <div className="feature-icon margin-bottom-20">
                          <span className="bi bi-envelope b-radius-50 d-block"></span>
                        </div>
                        <div className="feature-content">
                          <div className="feature-title relative margin-bottom-10">
                            <h5>Email Address</h5>
                          </div>
                          <span className="mb-0">
                            <a href={`mailto:${sitedata?.business_email || sitedata?.site_email}`}>
                              {sitedata?.business_email || sitedata?.site_email || "--"}
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="mb-4 mt-0" />
              </div>
              <div className="contact-form" data-animation="fadeInLeft">
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  role="form"
                  name="contactform"
                  id="contactform"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <div className="input-text form-group mb-4">
                        <input
                          type="text"
                          name="fullname"
                          className="input-name form-control"
                          placeholder="Full Name"
                          value={contactForm.fullname}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="input-email form-group">
                        <input
                          type="text"
                          name="email"
                          className="input-email form-control mb-4"
                          placeholder="Email"
                          value={contactForm.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="input-email form-group mb-4">
                        <input
                          type="text"
                          name="phone"
                          className="input-phone form-control"
                          placeholder="Phone"
                          value={contactForm.phone}
                          onChange={handleInputChange}
                          maxLength={14}
                          minLength={10}
                        />
                      </div>
                      <div className="textarea-message form-group mb-4">
                        <textarea
                          name="message"
                          className="textarea-message form-control"
                          placeholder="Message"
                          rows="4"
                          value={contactForm.message}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>
                      <button
                        className="btn btn-default btn-block "
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Send Now"}{" "}
                        <i className="icon-paper-plane ms-1"></i>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-map py-5">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="screen-reader-text">Map</h2>
                <div className="container-fluid pad-none">
                  <div className="map mt-0">
                    {/* <div
                      className="cs_map contact-map b-radius-0"
                      style={{ width: "100%", height: "400px" }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            contactData &&
                            contactData.length > 0 &&
                            contactData[0].google_map
                              ? contactData[0].google_map
                              : "",
                        }}
                      />
                    </div> */}
                    {contactData &&
                      contactData.length > 0 &&
                      contactData[0].google_map &&
                      contactData[0].google_map.includes("google.com/maps") ? (
                      <div
                        className="cs_map contact-map b-radius-0"
                        style={{ width: "100%", height: "400px" }}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: contactData[0].google_map,
                          }}
                        />
                      </div>
                    ) : null}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <FooterWrapper />
      </div>
    </div>
  );
}

export default Style4Contactus;